import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  ActionIcon,
  Menu,
  TextInput,
  Checkbox,
  Badge,
  Paper,
  Breadcrumbs,
  Anchor,
  Tooltip,
  SegmentedControl,
} from '@mantine/core';
import {
  IconFolder,
  IconFile,
  IconFileText,
  IconPhoto,
  IconVideo,
  IconMusic,
  IconFileZip,
  IconFileSpreadsheet,
  IconSearch,
  IconPlus,
  IconUpload,
  IconFolderPlus,
  IconDots,
  IconEdit,
  IconTrash,
  IconDownload,
  IconCopy,
  IconCut,
  IconClipboard,
  IconShare,
  IconStar,
  IconStarFilled,
  IconList,
  IconGridDots,
  IconSortAscending,
  IconSortDescending,
  IconChevronRight,
  IconHome,
} from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn, HoverLift, StaggeredList } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface FileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  mimeType?: string;
  size?: number;
  createdAt?: Date;
  modifiedAt?: Date;
  starred?: boolean;
  shared?: boolean;
  path?: string;
  thumbnail?: string;
  metadata?: Record<string, unknown>;
}

export interface FileBrowserProps {
  files: FileItem[];
  currentPath?: string[];
  onNavigate?: (path: string[]) => void;
  onFileClick?: (file: FileItem) => void;
  onFolderClick?: (folder: FileItem) => void;
  onFileSelect?: (files: FileItem[]) => void;
  onUpload?: () => void;
  onNewFolder?: () => void;
  onRename?: (file: FileItem) => void;
  onDelete?: (files: FileItem[]) => void;
  onDownload?: (files: FileItem[]) => void;
  onShare?: (file: FileItem) => void;
  onStar?: (file: FileItem) => void;
  onCopy?: (files: FileItem[]) => void;
  onCut?: (files: FileItem[]) => void;
  onPaste?: () => void;
  canPaste?: boolean;
  selectable?: boolean;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  sortBy?: 'name' | 'date' | 'size' | 'type';
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (sortBy: string, direction: 'asc' | 'desc') => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  className?: string;
}

export const FileBrowser = forwardRef<HTMLDivElement, FileBrowserProps>(
  (
    {
      files,
      currentPath = [],
      onNavigate,
      onFileClick,
      onFolderClick,
      onFileSelect,
      onUpload,
      onNewFolder,
      onRename,
      onDelete,
      onDownload,
      onShare,
      onStar,
      onCopy,
      onCut,
      onPaste,
      canPaste = false,
      selectable = true,
      viewMode = 'grid',
      onViewModeChange,
      sortBy = 'name',
      sortDirection = 'asc',
      onSortChange,
      searchQuery = '',
      onSearchChange,
      loading = false,
      className,
    },
    ref
  ) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const getFileIcon = (file: FileItem) => {
      if (file.type === 'folder') return <IconFolder size={24} color="var(--mantine-color-blue-5)" />;

      const mimeType = file.mimeType || '';
      if (mimeType.startsWith('image/')) return <IconPhoto size={24} color="var(--mantine-color-green-5)" />;
      if (mimeType.startsWith('video/')) return <IconVideo size={24} color="var(--mantine-color-red-5)" />;
      if (mimeType.startsWith('audio/')) return <IconMusic size={24} color="var(--mantine-color-violet-5)" />;
      if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <IconFileSpreadsheet size={24} color="var(--mantine-color-green-5)" />;
      if (mimeType.includes('zip') || mimeType.includes('compressed')) return <IconFileZip size={24} color="var(--mantine-color-orange-5)" />;
      if (mimeType.includes('text') || mimeType.includes('document')) return <IconFileText size={24} color="var(--mantine-color-blue-5)" />;

      return <IconFile size={24} color="var(--mantine-color-gray-5)" />;
    };

    const formatFileSize = (bytes?: number) => {
      if (!bytes) return '-';
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(1)} ${units[unitIndex]}`;
    };

    const formatDate = (date?: Date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    const handleSelect = (id: string) => {
      const newSelected = selectedIds.includes(id)
        ? selectedIds.filter((i) => i !== id)
        : [...selectedIds, id];
      setSelectedIds(newSelected);
      onFileSelect?.(files.filter((f) => newSelected.includes(f.id)));
    };

    const handleSelectAll = () => {
      if (selectedIds.length === files.length) {
        setSelectedIds([]);
        onFileSelect?.([]);
      } else {
        const allIds = files.map((f) => f.id);
        setSelectedIds(allIds);
        onFileSelect?.(files);
      }
    };

    const handleItemClick = (file: FileItem) => {
      if (file.type === 'folder') {
        onFolderClick?.(file);
        onNavigate?.([...currentPath, file.name]);
      } else {
        onFileClick?.(file);
      }
    };

    const handleBreadcrumbClick = (index: number) => {
      onNavigate?.(currentPath.slice(0, index + 1));
    };

    const selectedFiles = files.filter((f) => selectedIds.includes(f.id));

    const renderGridItem = (file: FileItem) => (
      <HoverLift lift={4}>
        <Paper
          p="md"
          withBorder
          style={{
            cursor: 'pointer',
            backgroundColor: selectedIds.includes(file.id)
              ? 'var(--mantine-color-blue-0)'
              : undefined,
            borderColor: selectedIds.includes(file.id)
              ? 'var(--mantine-color-blue-5)'
              : undefined,
          }}
          onClick={() => handleItemClick(file)}
        >
          <Stack gap="xs" align="center">
            {selectable && (
              <Checkbox
                checked={selectedIds.includes(file.id)}
                onChange={() => handleSelect(file.id)}
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: 8, left: 8 }}
              />
            )}
            {file.thumbnail ? (
              <Box
                style={{
                  width: 60,
                  height: 60,
                  backgroundImage: `url(${file.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: 4,
                }}
              />
            ) : (
              getFileIcon(file)
            )}
            <Box ta="center">
              <Group gap={4} justify="center">
                <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
                  {file.name}
                </Text>
                {file.starred && <IconStarFilled size={12} color="var(--mantine-color-yellow-5)" />}
                {file.shared && <IconShare size={12} color="var(--mantine-color-blue-5)" />}
              </Group>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {file.type === 'folder' ? 'Folder' : formatFileSize(file.size)}
              </Text>
            </Box>
          </Stack>
        </Paper>
      </HoverLift>
    );

    const renderListItem = (file: FileItem) => (
      <HoverLift lift={2}>
        <Paper
          p="sm"
          withBorder
          style={{
            cursor: 'pointer',
            backgroundColor: selectedIds.includes(file.id)
              ? 'var(--mantine-color-blue-0)'
              : undefined,
          }}
          onClick={() => handleItemClick(file)}
        >
          <Group justify="space-between">
            <Group gap="sm">
              {selectable && (
                <Checkbox
                  checked={selectedIds.includes(file.id)}
                  onChange={() => handleSelect(file.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {getFileIcon(file)}
              <Box>
                <Group gap={4}>
                  <Text size="sm" fw={500} style={accessibilityStyles}>{file.name}</Text>
                  {file.starred && <IconStarFilled size={12} color="var(--mantine-color-yellow-5)" />}
                  {file.shared && <Badge size="xs" variant="light">Shared</Badge>}
                </Group>
              </Box>
            </Group>
            <Group gap="xl">
              <Text size="sm" c="dimmed" w={100} style={accessibilityStyles}>
                {file.type === 'folder' ? '-' : formatFileSize(file.size)}
              </Text>
              <Text size="sm" c="dimmed" w={120} style={accessibilityStyles}>
                {formatDate(file.modifiedAt)}
              </Text>
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {onDownload && file.type === 'file' && (
                    <Menu.Item leftSection={<IconDownload size={14} />} onClick={() => onDownload([file])}>
                      Download
                    </Menu.Item>
                  )}
                  {onShare && (
                    <Menu.Item leftSection={<IconShare size={14} />} onClick={() => onShare(file)}>
                      Share
                    </Menu.Item>
                  )}
                  {onStar && (
                    <Menu.Item
                      leftSection={file.starred ? <IconStarFilled size={14} /> : <IconStar size={14} />}
                      onClick={() => onStar(file)}
                    >
                      {file.starred ? 'Unstar' : 'Star'}
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  {onRename && (
                    <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onRename(file)}>
                      Rename
                    </Menu.Item>
                  )}
                  {onCopy && (
                    <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => onCopy([file])}>
                      Copy
                    </Menu.Item>
                  )}
                  {onCut && (
                    <Menu.Item leftSection={<IconCut size={14} />} onClick={() => onCut([file])}>
                      Cut
                    </Menu.Item>
                  )}
                  {onDelete && (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => onDelete([file])}>
                        Delete
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Paper>
      </HoverLift>
    );

    return (
      <FadeIn>
        <Card ref={ref} className={className} p={0}>
          <Stack gap={0}>
            {/* Toolbar */}
            <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Group justify="space-between">
                <Group gap="sm">
                  {onUpload && (
                    <Button leftSection={<IconUpload size={16} />} onClick={onUpload}>
                      Upload
                    </Button>
                  )}
                  {onNewFolder && (
                    <Button variant="subtle" leftSection={<IconFolderPlus size={16} />} onClick={onNewFolder}>
                      New Folder
                    </Button>
                  )}
                  {selectedIds.length > 0 && (
                    <>
                      {onDownload && (
                        <Button variant="subtle" leftSection={<IconDownload size={16} />} onClick={() => onDownload(selectedFiles)}>
                          Download
                        </Button>
                      )}
                      {onDelete && (
                        <Button variant="subtle" color="red" leftSection={<IconTrash size={16} />} onClick={() => onDelete(selectedFiles)}>
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                  {canPaste && onPaste && (
                    <Button variant="subtle" leftSection={<IconClipboard size={16} />} onClick={onPaste}>
                      Paste
                    </Button>
                  )}
                </Group>
                <Group gap="sm">
                  {onSearchChange && (
                    <TextInput
                      placeholder="Search files..."
                      leftSection={<IconSearch size={16} />}
                      value={searchQuery}
                      onChange={(e) => onSearchChange(e.target.value)}
                      style={{ width: 200 }}
                    />
                  )}
                  {onViewModeChange && (
                    <SegmentedControl
                      value={viewMode}
                      onChange={(value) => onViewModeChange(value as 'grid' | 'list')}
                      data={[
                        { value: 'grid', label: <IconGridDots size={16} /> },
                        { value: 'list', label: <IconList size={16} /> },
                      ]}
                      size="xs"
                    />
                  )}
                </Group>
              </Group>
            </Box>

            {/* Breadcrumbs */}
            <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Breadcrumbs separator={<IconChevronRight size={14} />}>
                <Anchor onClick={() => onNavigate?.([])}>
                  <Group gap={4}>
                    <IconHome size={14} />
                    Home
                  </Group>
                </Anchor>
                {currentPath.map((segment, index) => (
                  <Anchor key={index} onClick={() => handleBreadcrumbClick(index)}>
                    {segment}
                  </Anchor>
                ))}
              </Breadcrumbs>
            </Box>

            {/* File list */}
            <Box p="md">
              {viewMode === 'grid' ? (
                <StaggeredList staggerDelay={30}>
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 16,
                    }}
                  >
                    {files.map((file) => (
                      <Box key={file.id}>{renderGridItem(file)}</Box>
                    ))}
                  </Box>
                </StaggeredList>
              ) : (
                <Stack gap="xs">
                  {/* Header */}
                  <Group justify="space-between" px="sm">
                    <Group gap="sm">
                      {selectable && (
                        <Checkbox
                          checked={selectedIds.length === files.length && files.length > 0}
                          indeterminate={selectedIds.length > 0 && selectedIds.length < files.length}
                          onChange={handleSelectAll}
                        />
                      )}
                      <Text size="sm" fw={500} c="dimmed" style={accessibilityStyles}>Name</Text>
                    </Group>
                    <Group gap="xl">
                      <Text size="sm" fw={500} c="dimmed" w={100} style={accessibilityStyles}>Size</Text>
                      <Text size="sm" fw={500} c="dimmed" w={120} style={accessibilityStyles}>Modified</Text>
                      <Box w={28} />
                    </Group>
                  </Group>
                  <StaggeredList staggerDelay={20}>
                    {files.map((file) => (
                      <Box key={file.id}>{renderListItem(file)}</Box>
                    ))}
                  </StaggeredList>
                </Stack>
              )}

              {files.length === 0 && (
                <Box py="xl" ta="center">
                  <IconFolder size={48} color="var(--mantine-color-gray-4)" />
                  <Text c="dimmed" mt="sm" style={accessibilityStyles}>This folder is empty</Text>
                  {onUpload && (
                    <Button variant="subtle" mt="sm" onClick={onUpload}>
                      Upload files
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* Status bar */}
            <Box px="md" py="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {files.length} items
                {selectedIds.length > 0 && ` • ${selectedIds.length} selected`}
              </Text>
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

FileBrowser.displayName = 'FileBrowser';

export default FileBrowser;
